/**
 * Pure helpers for the Misc Task Trending card on the Reports hub.
 *
 * Split out of ReportsHubPage.tsx so the derived logic can be tested directly —
 * the same arrangement as reportsHubHelpers.ts. Nothing here touches React or
 * the DOM.
 */
import { MiscTaskTrendSupervisor } from '../../api/miscTaskTrending';

/**
 * The label for one supervisor, used for BOTH the picker's menu rows and the
 * trigger's current-value text, so the two can never read differently.
 *
 * The employee number is not decoration: NPM has genuinely duplicated names in
 * the employee table, and it is what makes two "GARCIA CABRERA" rows tellable
 * apart at selection time. The direct-report count does the same job and is
 * carried separately as FloatMenuButton's `count`, which renders it right-aligned
 * on the menu row — the trigger has no room for it.
 */
export function supervisorLabel(option: MiscTaskTrendSupervisor): string {
    return option.altEmployeeID ? `${option.name} (${option.altEmployeeID})` : option.name;
}

/**
 * Which supervisor the picker should open on.
 *
 * The API's `defaultEmployeeID` is the caller when they supervise anyone. This
 * re-validates it against the options actually returned rather than trusting it
 * blindly — a `<select>` whose value matches no `<option>` renders blank, and a
 * blank picker beside an enabled Download button is the worst of both.
 */
export function resolveDefaultSupervisor(
    defaultEmployeeID: number | null,
    options: MiscTaskTrendSupervisor[]
): number | null {
    if (options.length === 0) return null;
    if (
        defaultEmployeeID != null &&
        options.some((option) => option.employeeID === defaultEmployeeID)
    ) {
        return defaultEmployeeID;
    }
    return options[0].employeeID;
}

/**
 * `MiscTaskTrending_CORTEZ_JESSE_Week36_2026.pdf`
 *
 * BUILT CLIENT-SIDE ON PURPOSE. The API sets a `Content-Disposition` filename,
 * but `content-disposition` is not a CORS-safelisted response header, so on a
 * cross-origin XHR the browser hides it from JS unless the server also sends
 * `Access-Control-Expose-Headers`. The AdminApp and the API are on different
 * origins in every environment including production, so reading the header
 * yielded `MiscTaskTrending.pdf` every time — and a user pulling reports for four
 * supervisors got four indistinguishable files. `downloadReportPdf` in
 * api/audit.ts already names files this way; this matches it.
 *
 * Mirrors `buildMiscTaskTrendFileName` in the API's miscTaskTrendPdfGenerator.ts.
 */
export function miscTaskTrendFileName(
    supervisorName: string,
    weekNumber: number | null | undefined,
    accountingYear: number | string | null | undefined,
    format: 'pdf' | 'xlsx'
): string {
    // Runs of punctuation/space collapse to ONE underscore, so "CORTEZ, JESSE"
    // reads "CORTEZ_JESSE" rather than "CORTEZJESSE" or "CORTEZ__JESSE". Leading
    // and trailing separators are trimmed so the name never starts or ends with _.
    const who =
        (supervisorName ?? '')
            .replace(/[^A-Za-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '') || 'Supervisor';
    const when = weekNumber != null ? `Week${weekNumber}_${accountingYear ?? 'NA'}` : 'NoPeriod';
    return `MiscTaskTrending_${who}_${when}.${format}`;
}

/**
 * The four sentences describing what the report counts.
 *
 * WORD FOR WORD the same as ATTRIBUTION_CAVEAT in the API's
 * src/util/miscTaskTrendPdfGenerator.ts, which prints on every page of the PDF
 * and in the workbook's run-info block. Historical attribution reads as a bug
 * the first time a supervisor sees dollars from a crew they no longer have —
 * on real NPM data that happens in week one — so the explanation has to be in
 * front of them BEFORE they download, not only inside the file. If either copy
 * changes, change both.
 */
export const MISC_TASK_TREND_INCLUSIONS: string[] = [
    'Approved claims only. Pending and denied misc tasks are left out.',
    "A claim counts against the supervisor recorded on that employee's timesheet for that week, not their supervisor today.",
    'An employee who changed supervisors mid-window appears in both sections, split by week.',
    'Claims from a week with no submitted timesheet appear under Unassigned, and employees with no approved claims are omitted.',
];
